import graphql from "babel-plugin-relay/macro";
import { useAtomValue, useSetAtom } from "jotai";
import { useFragment } from "react-relay";

import {
  DropdownSelect,
  DropdownSelectOption,
} from "@whoosh/design-system/src/components/controls";
import { GetTransportIconFromCode } from "@whoosh/design-system/src/components/icons_v2/transportation";
import { VStack } from "@whoosh/design-system/src/components/primitive";
import { useGetClubCurrency } from "@whoosh/features/src/ClubCurrency";
import { ConvertCentsToDollars } from "@whoosh/utilities/src/Currency";

import { setTransportationIdTypeOnRoundAtom } from "../../../../../../atoms/actions/setTransportationTypeIdOnRoundAtom/setTransportationTypeIdOnRoundAtom";
import { getTransportationTypeIdOnRoundAtom } from "../../../../../../atoms/selectors/getTransportationTypeIdOnRoundAtom/getTransportationTypeIdOnRoundAtom";
import { ConfigureRoundTransportationTypeControl_Booking$key } from "./__generated__/ConfigureRoundTransportationTypeControl_Booking.graphql";

type Props = {
  bookingRef: ConfigureRoundTransportationTypeControl_Booking$key | null;
  withLabel?: boolean;
};

export const ConfigureRoundTransportationTypeControl = ({
  bookingRef,
  withLabel = false,
}: Props) => {
  const { currencySymbol } = useGetClubCurrency();
  const booking = useFragment(
    ConfigureRoundTransportationTypeControlFragmentString,
    bookingRef
  );

  const selectedTransportationTypeId = useAtomValue(
    getTransportationTypeIdOnRoundAtom
  );

  const setTransportationIdTypeOnRound = useSetAtom(
    setTransportationIdTypeOnRoundAtom
  );
  const permittedTransportationTypes = booking?.startSlot?.transportationTypes;
  const isPrivateInviteFlow =
    booking?.rounds?.edges?.some(
      (round) => round?.node?.__typename === "MemberRound"
    ) ?? false;

  const dropdownOptions: DropdownSelectOption<string>[] =
    permittedTransportationTypes?.flatMap((tt) => {
      if (!tt || !tt?.id) return [];
      const icon = GetTransportIconFromCode(tt.code ?? null);
      const priceInCents = tt.eighteenHolePrice ?? 0;
      const priceDisplay = ConvertCentsToDollars(priceInCents);
      return {
        value: tt.id,
        leftElement: icon ? icon({}) : undefined,
        label: tt.name || "",
        subLabel: isPrivateInviteFlow
          ? undefined
          : `${currencySymbol}${priceDisplay}`,
      };
    }) ?? [];
  return (
    <VStack
      flex={withLabel ? 1 : undefined}
      width={withLabel ? undefined : 108}
    >
      <DropdownSelect
        hideOuterMargin
        hideChevron
        hideLabelInDisplay={!withLabel}
        includeLeftElementInSelectedValue
        contentSizeOverride="regular"
        options={dropdownOptions}
        value={selectedTransportationTypeId}
        onChange={(transportationTypeId) => {
          setTransportationIdTypeOnRound({
            transportationTypeId: transportationTypeId ?? "",
          });
        }}
      />
    </VStack>
  );
};

const ConfigureRoundTransportationTypeControlFragmentString = graphql`
  fragment ConfigureRoundTransportationTypeControl_Booking on Booking {
    startSlot {
      transportationTypes(memberVisible: true) {
        id
        code
        name
        customCode
        eighteenHolePrice: totalPrice(holeCount: EIGHTEEN)
        nineHolePrice: totalPrice(holeCount: NINE)
      }
    }
    rounds {
      edges {
        node {
          __typename
        }
      }
    }
  }
`;
